import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { EntityList, InitialsAvatar, PageBackground } from '../shared';
import { usePeopleTableConfig, usePeopleFilters } from './hooks';
import { usePeopleDataContext } from '../../contexts/PeopleDataContext';
import { Person } from '../../types/people';

const People: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Custom hooks for data and filtering
  const { data, loading, error, loadingMore } = usePeopleDataContext();
  const columns = usePeopleTableConfig();
  const { filteredData, filterDefinitions, setAppliedFilters } = usePeopleFilters(data, searchQuery);

  // Handlers
  const handleCreatePerson = () => {
    console.log('Create person');
  };

  const handleDownloadCSV = () => {
    console.log('Download CSV');
  };

  const handlePrint = () => {
    console.log('Print');
  };

  const handleRowClick = (person: any) => {
    navigate(`/community/detail/${person.id}`);
  };

  // Show loading state with page structure
  if (loading) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-4 sm:mb-8">
            <div className="h-8 bg-gray-200 rounded w-32 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
          
          {/* Search bar skeleton */}
          <div className="h-10 bg-gray-200 rounded w-full mb-4 animate-pulse"></div>
          
          {/* Table skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </PageBackground>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center max-w-md">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <LucideIcons.AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load People</h3>
                <p className="text-red-700 mb-4">{error.message}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }

  // Gallery tile renderer for person cards
  const renderPersonGalleryTile = (person: Person) => {
    const fullName = `${person.firstName} ${person.lastName}`;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col">
        {/* Header with gradient */}
        <div className="h-10 bg-gradient-to-br from-blue-200 to-purple-200 flex-shrink-0" />
        
        {/* Content */}
        <div className="p-4 -mt-10 flex-1 flex flex-col">
          {/* Avatar */}
          <div className="flex justify-center mb-3 flex-shrink-0">
            <div className="border-4 border-white shadow-md rounded-full">
              <InitialsAvatar 
                personId={person.id} 
                name={fullName} 
                avatarUrl={person.avatarUrl}
                size={80} 
              />
            </div>
          </div>

          {/* Name */}
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-1 truncate flex-shrink-0">
            {fullName}
          </h3>

          {/* Contact Info - Fixed height container */}
          <div className="space-y-1.5 flex-shrink-0" style={{ minHeight: '84px' }}>
            {person.email && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <LucideIcons.Mail size={14} className="flex-shrink-0 text-gray-400 mt-0.5" />
                <span className="truncate">{person.email}</span>
              </div>
            )}
            {person.phoneNumber && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <LucideIcons.Phone size={14} className="flex-shrink-0 text-gray-400 mt-0.5" />
                <span className="truncate">{person.phoneNumber}</span>
              </div>
            )}
            {person.address && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <LucideIcons.MapPin size={14} className="flex-shrink-0 text-gray-400 mt-0.5" />
                <span className="line-clamp-2">{person.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <EntityList<Person>
      // Page configuration
      objectTitle="People"
      
      // Primary action
      primaryAction={{
        label: 'Add Person',
        icon: LucideIcons.UserPlus,
        onClick: handleCreatePerson
      }}
      
      // Additional menu items
      kebabMenuItems={[
        {
          label: 'Import People',
          icon: LucideIcons.Upload,
          onClick: () => console.log('Import people')
        },
        {
          label: 'Merge Duplicates',
          icon: LucideIcons.Users,
          onClick: () => console.log('Merge duplicates')
        }
      ]}
      
      // Search
      searchPlaceholder="Search by name, email, etc..."
      onSearchChange={setSearchQuery}
      
      // View configuration
      enableGalleryView={true}
      defaultView="table"
      
      // Table configuration
      columns={columns}
      getRowKey={(person) => person.id}
      emptyMessage="No people found"
      pageSize={10}
      onRowClick={handleRowClick}
      
      // Gallery configuration
      renderGalleryTile={renderPersonGalleryTile}
      galleryPageSize={12}
      
      // Data
      filteredData={filteredData}
      
      // Filters
      filterDefinitions={filterDefinitions}
      onApplyFilters={setAppliedFilters}
      
      // Default actions
      onDownloadCSV={handleDownloadCSV}
      onPrint={handlePrint}
      
      // Loading state
      loadingMore={loadingMore}
    />
  );
};

export default People;
